import React, { useState, useCallback } from 'react';
import {
  Card, Form, InputGroup, Spinner, ListGroup,
} from 'react-bootstrap';
import _ from 'lodash';
import dayjs from 'dayjs';

import BookingService from '../../../Services/BookingService';

function PassengerSearch({ eventName, onBookingClick }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Debounced search function
  const debouncedSearch = useCallback(
    _.debounce(async (term, event) => {
      if (!term || term.length < 2 || !event) {
        setResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      try {
        const searchResults = await BookingService.searchEventPassengers(event, term);
        setResults(searchResults);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error searching passengers:', err);
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    [],
  );

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    debouncedSearch(term, eventName);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setResults([]);
  };

  return (
    <Card className="mb-3">
      <Card.Header>
        <i className="fa fa-search me-2" />
        Passenger Search
      </Card.Header>
      <Card.Body>
        <InputGroup className="mb-2">
          <Form.Control
            type="text"
            placeholder="Search by name, flight..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
          {searchTerm && (
            <InputGroup.Text
              style={{ cursor: 'pointer' }}
              onClick={clearSearch}
            >
              <i className="fa fa-times" />
            </InputGroup.Text>
          )}
        </InputGroup>

        {isSearching && (
          <div className="text-center py-3">
            <Spinner size="sm" />
            <span className="ms-2 text-muted">Searching...</span>
          </div>
        )}

        {!isSearching && searchTerm && results.length === 0 && (
          <div className="text-center py-3 text-muted">
            <i className="fa fa-user-slash me-2" />
            No passengers found
          </div>
        )}

        {results.length > 0 && (
          <ListGroup className="passenger-search-results">
            {results.map((result, idx) => (
              <ListGroup.Item
                key={`result-${idx + 1}`}
                action
                onClick={() => onBookingClick(result.booking)}
                className="d-flex flex-column"
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <span className="fw-bold">
                      {result.passengerName}
                    </span>
                    {result.isFromManifest && (
                      <small className="text-info ms-2">
                        (manifest)
                      </small>
                    )}
                  </div>
                  <small className="text-muted">
                    {result.booking?.booking_reference}
                  </small>
                </div>
                <small className="text-muted">
                  {result.booking?.date_datetime && (
                    <span className="me-2">
                      <i className="fa fa-clock me-1" />
                      {dayjs(result.booking.date_datetime).format('MMM D, HH:mm')}
                    </span>
                  )}
                  {result.flightNumber && (
                    <span className="me-2">
                      <i className="fa fa-plane me-1" />
                      {result.flightNumber}
                    </span>
                  )}
                  {result.booking?.event_group && (
                    <span>
                      <i className="fa fa-users me-1" />
                      {result.booking.event_group}
                    </span>
                  )}
                </small>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}

        <Form.Text className="text-muted">
          <i className="fa fa-info-circle me-1" />
          Search across booking names and passenger manifests
        </Form.Text>
      </Card.Body>
    </Card>
  );
}

export default PassengerSearch;
